import { Injectable } from '@angular/core';
import { CognitoUser, CognitoUserPool, AuthenticationDetails, CognitoUserAttribute, CognitoRefreshToken, ICognitoUserAttributeData } from 'amazon-cognito-identity-js'
import { AvantoSessionService } from './avanto-session.service';


@Injectable({
  providedIn: 'root'
})
export class AvantoCognitoService {
  authDetails: AuthenticationDetails;

  readonly COGNITO_USER_POOL: CognitoUserPool = new CognitoUserPool({ UserPoolId: "us-east-1_tyc8JOcQy", ClientId: "55vrjk98bn31iu2v7aa73li2d2" });

  constructor(private avantoSession: AvantoSessionService) { }

  getCognitoUser() {
    return new Promise<CognitoUser>((resolve, reject) => {
      const cognitoUser = this.COGNITO_USER_POOL.getCurrentUser();
      if (cognitoUser != null) {
        resolve(cognitoUser);
      } else {
        reject("User not found.");
      }
    })
  }

  getNewCognitoUser(username): CognitoUser {
    return new CognitoUser({
      Username: username,
      Pool: this.COGNITO_USER_POOL
    });
  }


  //================= SIGN IN =================\\
  /**
   * Logs the user in with cognito
   * @param login User login
   * @param password User password
   */
  login(login, password) {
    return new Promise((resolve, reject) => {

      const authenticationDetails = new AuthenticationDetails({
        Username: login,
        Password: password,
      });

      const cognitoUser: CognitoUser = this.getNewCognitoUser(login);

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: function (result) {
          resolve(result);
        },
        onFailure: function (err) {
          reject(err)
        }
      });
    });
  }

  //================= CUSTOMER INFO =================\\
  changeCustomerInfo(newName) {
    return new Promise((resolve, reject) => {
      this.getCognitoUser().then(user => {
        user.getSession(() => {
          var attributeList: ICognitoUserAttributeData[] = [{ Name: 'name', Value: newName }];
          user.updateAttributes(attributeList, (err, result) => {
            if (err) {
              console.error(err);
              reject(err);
            } else {
              this.refreshSession();
              resolve();
            }
          })
        })
      })
        .catch((err) => { reject(err) })
    });
  }

  verifyMail() {
    return new Promise((resolve, reject) => {
      this.getCognitoUser().then(user => {
        user.getSession(() => {
          user.getAttributeVerificationCode("email", {
            onSuccess: () => { resolve(); },
            onFailure: (failure) => {
              console.error(failure);
              reject(failure);
            }
          })
        })
      })
        .catch((err) => { reject(err) })
    });
  }

  /**
   * Refreshes the session, updating it with the latest customer information
   */
  refreshSession() {
    return new Promise((resolve, reject) => {
      this.getCognitoUser().then(user => {
        user.getSession((err, session) => {
          if (err) {
            reject(err)
          } else if (!session.isValid()) {
            reject("Invalid session");
          } else {
            user.refreshSession(new CognitoRefreshToken({ RefreshToken: session.refreshToken.token }), (err, res) => {
              if (err) {
                reject(err)
              } else {
                resolve(res)
              }
            })
          }
        })
      })
        .catch((err) => { console.error(err); reject(err) })
    });
  }

  /** ... pretty obvious ... */
  logout() {
    return new Promise((resolve) => {
      this.avantoSession.clearSession().finally(() => {
        this.getCognitoUser().then(user => {
          user.signOut();
          resolve()
        })
      })
    })
  }


  //================= PASSWORD =================\\

  /**
   * Called whenever the user forgets his password.
   * Cognito sends a verification code to the user's phone. 
   * The verification code will be validated inn another method, described below
   * @param username login, registered for the user in cognito (Right now we are using the phone for login. Might change that to phone and e-mail later)
   */
  forgotPassword(username) {
    return new Promise((resolve, reject) => {
      const user = this.getNewCognitoUser(username);
      user.forgotPassword({
        onSuccess: (data) => { resolve(data) },
        onFailure: (err) => { reject(err) }
      })
    });
  }

  /**
   * After the user selects the "forgotPassword" option, he will receive a verification code from amazon.
   * This method will be used in conjunction with this code to reset the user's password
   * @param username login, registered for the user in cognito (Right now we are using the phone for login. Might change that to phone and e-mail later)
   * @param verificationCode verification code received as an SMS
   * @param newPassword new password to be used by the user
   */
  confirmForgottenPassword(username, verificationCode, newPassword) {
    return new Promise((resolve, reject) => {
      const user = this.getNewCognitoUser(username);
      user.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => { resolve() },
        onFailure: (err) => { reject(err) }
      })
    });
  }

  /**
   * Requests that the password is changed
   * @param username login, registered for the user in cognito
   * @param oldPassword Old password
   * @param newPassword New password that will be changed to
   */
  changePassword(oldPassword, newPassword) {
    return new Promise((resolve, reject) => {
      this.getCognitoUser().then(user => {
        user.getSession((err) => {
          if (err) {
            reject(err)
          }

          user.changePassword(oldPassword, newPassword, (err, success) => {
            if (err) { reject(err) }
            else { resolve(success) }
          })
        })
      })
        .catch((err) => { reject(err) })
    })
  }


  //================= SIGN UP =================\\

  /**
   * Registers a new user into our system
   * @param email User's e-mail
   * @param phone User's phone, on the format +1647999999
   * @param name User's name
   * @param password User's password
   */
  signUp(userEmail, userPhone, userName, userPassword) {
    let attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: userEmail }),
      new CognitoUserAttribute({ Name: 'phone_number', Value: userPhone }),
      new CognitoUserAttribute({ Name: 'name', Value: userName })
    ];
    return new Promise((resolve, reject) => {
      this.COGNITO_USER_POOL.signUp(userPhone, userPassword, attributeList, null, (err, result) => {
        if (err) { reject(err); }
        else { resolve(result); }
      })
    });
  }


  /**
   * After the user signs up, a verification code will be sent to them.
   * This method checks if the verification code is correct or not
   * @param username login, registered for the user in cognito (Right now we are using the phone for login. Might change that to phone and e-mail later)
   * @param confirmationCode verification code sent to the user
   */
  confirmSignUp(username: string, confirmationCode: string) {
    return new Promise((resolve, reject) => {
      const user = this.getNewCognitoUser(username);
      user.confirmRegistration(confirmationCode, true, (err, result) => {
        if (err) { reject(err) }
        else { resolve(result) }
      })
    })
  }

  /**
   * Confirms the user's e-mail with his received verificationCode
   * @param username login, registered for the user in cognito (Right now we are using the phone for login. Might change that to phone and e-mail later)
   * @param confirmationCode verification code sent to the user
   */
  confirmEmailVerificationCode(username: string, confirmationCode: string) {
    return new Promise((resolve, reject) => {
      this.getCognitoUser().then(user => {
        user.getSession(() => {
          user.verifyAttribute("email", confirmationCode, {
            onSuccess: (data) => { 
              resolve(data);
              this.refreshSession();
            },
            onFailure: (err) => { reject(err) }
          });
        })
      })
        .catch((err) => { reject(err) })
    });
  }

  /**
   * Requests that another verification code (the one you need for sign up) is sent
   * @param username login, registered for the user in cognito (Right now we are using the phone for login. Might change that to phone and e-mail later)
   */
  reSendVerificationCode(username: string) {
    return new Promise((resolve, reject) => {
      const user = this.getNewCognitoUser(username);
      user.resendConfirmationCode((err, result) => {
        if (err) { reject(err) }
        else { resolve(result) }
      })
    })
  }
}